import React, { useState, useEffect, useContext, useMemo } from "react";
import { showNotice } from "../helper/notify";
import {
  Button,
  Col,
  Row,
  Table,
  Tag,
  Select,
  Input,
  InputNumber,
  Switch,
  Upload,
  message,
  Popconfirm,
  Tooltip,
  Modal,
  Form,
  Space,
  Badge,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  PlayCircleOutlined,
  CameraOutlined,
  ThunderboltOutlined,
  HolderOutlined,
  UploadOutlined,
  PictureOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import PageHeader from "../components/generic/PageHeader";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getUISteps,
  addUIStep,
  updateUIStep,
  deleteUIStep,
  reorderUISteps,
  runUISuite,
  getUIActions,
  uploadUIAsset,
  getUIAssetUrl,
} from "../service/uitest.service";
import { pollJobStatus } from "../service/run.service";
import { useUser } from "../context/UserContext";
import CitrixRunModal from "../components/modal/CitrixRunModal";

const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_COLORS = {
  // Web (Playwright)
  Navigation: "blue",
  Interaction: "green",
  Waiting: "orange",
  Assert: "purple",
  Screenshot: "cyan",
  JavaScript: "red",
  Frames: "geekblue",
  Variables: "gold",
  // Citrix (OculiX)
  App: "magenta",
  Find: "green",
  Input: "blue",
  OCR: "purple",
  Evidence: "cyan",
  Timing: "orange",
};

// ─── Reference-image picker (Citrix image-locator steps) ──────
// Uploads to ARC storage and stores the returned image key as the field value.
function ReferenceImagePicker({ value, onChange }) {
  const [url, setUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let objectUrl;
    if (value) {
      getUIAssetUrl(value)
        .then((u) => {
          objectUrl = u;
          setUrl(u);
        })
        .catch(() => setUrl(null));
    } else {
      setUrl(null);
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [value]);

  const beforeUpload = (file) => {
    setUploading(true);
    uploadUIAsset(file)
      .then((res) => onChange?.(res.data.imageKey))
      .catch(() => message.error("Image upload failed"))
      .finally(() => setUploading(false));
    return false; // stop AntD's automatic upload
  };

  return (
    <div>
      <Upload
        beforeUpload={beforeUpload}
        showUploadList={false}
        accept="image/png,image/jpeg"
      >
        <Button icon={<UploadOutlined />} loading={uploading} size="small">
          {value ? "Replace image" : "Upload reference image"}
        </Button>
      </Upload>
      {url && (
        <div style={{ marginTop: 8 }}>
          <img
            src={url}
            alt="reference"
            style={{
              maxWidth: "100%",
              maxHeight: 160,
              border: "1px solid #eee",
              borderRadius: 4,
            }}
          />
        </div>
      )}
      {value && (
        <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
          <PictureOutlined /> {value}
        </div>
      )}
    </div>
  );
}

// ─── Drag handle context (mirrors MasterSuiteNewTable) ────────
const RowContext = React.createContext({});

const DraggableRow = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

function UIAutomationBuilder() {
  const { email } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const suite = location.state?.suite;

  const [steps, setSteps] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [running, setRunning] = useState(false);
  const [form] = Form.useForm();
  const [selectedAction, setSelectedAction] = useState(null);

  const engine = suite?.engine || "WEB";
  const isCitrix = engine === "CITRIX";

  // Citrix data-driven run modal (reusable component)
  const [runModalOpen, setRunModalOpen] = useState(false);

  useEffect(() => {
    if (!suite) {
      navigate("/ui-automation");
      return;
    }
    Promise.all([
      getUISteps(suite.suiteId),
      getUIActions(suite.engine || "WEB"),
    ])
      .then(([stepsRes, actionsRes]) => {
        setSteps(stepsRes.data || []);
        setActions(actionsRes.data || []);
      })
      .catch(() => message.error("Failed to load steps"))
      .finally(() => setLoading(false));
  }, [suite]);

  const fetchSteps = () => {
    getUISteps(suite.suiteId)
      .then((res) => setSteps(res.data || []))
      .catch(() => {});
  };

  const getActionMeta = (actionName) =>
    actions.find((a) => a.action === actionName) || {};

  const openAddModal = () => {
    setEditingStep(null);
    setSelectedAction(null);
    form.resetFields();
    form.setFieldsValue({
      screenshotOnFail: true,
      stopOnFail: true,
      similarity: 0.9,
    });
    setStepModalOpen(true);
  };

  const openEditModal = (step) => {
    setEditingStep(step);
    setSelectedAction(step.action);

    let cfg = {};
    if (step.engineConfig) {
      try {
        cfg = JSON.parse(step.engineConfig) || {};
      } catch {
        cfg = {};
      }
    }
    form.setFieldsValue({
      action: step.action,
      locator: step.locator,
      value: step.value,
      description: step.description,
      screenshotOnFail: step.screenshotOnFail,
      stopOnFail: step.stopOnFail,
      similarity: cfg.similarity ?? 0.9,
      region: cfg.region || "",
      ocrRegion: cfg.ocrRegion || "",
    });
    setStepModalOpen(true);
  };

  const handleSaveStep = () => {
    form
      .validateFields()
      .then((values) => {
        // ─── Extra validation based on action metadata ────────
        const meta = getActionMeta(values.action);

        if (meta.locatorRequired && !values.locator?.trim()) {
          form.setFields([
            {
              name: "locator",
              errors: [
                `${meta.locatorLabel || "Locator"} is required for ${values.action}`,
              ],
            },
          ]);
          return;
        }

        if (meta.valueRequired && !values.value?.trim()) {
          form.setFields([
            {
              name: "value",
              errors: [
                `${meta.valueLabel || "Value"} is required for ${values.action}`,
              ],
            },
          ]);
          return;
        }

        // For Citrix steps, fold the OculiX knobs into engineConfig JSON and
        // drop the standalone fields so they don't get persisted as columns.
        const { similarity, region, ocrRegion, ...rest } = values;
        const payload = { ...rest, suiteId: suite.suiteId };
        if (isCitrix) {
          const cfg = {};
          if (similarity != null) cfg.similarity = similarity;
          if (region?.trim()) cfg.region = region.trim();
          if (ocrRegion?.trim()) cfg.ocrRegion = ocrRegion.trim();
          payload.engineConfig = JSON.stringify(cfg);
        }
        const action = editingStep
          ? updateUIStep(suite.suiteId, editingStep.stepId, payload)
          : addUIStep(suite.suiteId, payload);

        action
          .then(() => {
            message.success(editingStep ? "Step updated" : "Step added");
            setStepModalOpen(false);
            fetchSteps();
          })
          .catch(() => message.error("Failed to save step"));
      })
      .catch(() => {
        // Form.validateFields rejection means there are validation errors
        // Ant Design shows them inline automatically
      });
  };

  const handleDeleteStep = (stepId) => {
    deleteUIStep(suite.suiteId, stepId)
      .then(() => {
        message.success("Step deleted");
        fetchSteps();
      })
      .catch(() => message.error("Failed to delete step"));
  };

  // ─── Drag and drop reorder ────────────────────────────────
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setSteps((prev) => {
      const activeIndex = prev.findIndex((s) => s.stepId === active.id);
      const overIndex = prev.findIndex((s) => s.stepId === over.id);
      const reordered = arrayMove(prev, activeIndex, overIndex).map((s, i) => ({
        ...s,
        seqNo: i + 1,
      }));
      // Persist reorder to backend
      const order = reordered.map((s) => ({
        stepId: s.stepId,
        seqNo: s.seqNo,
      }));
      reorderUISteps(suite.suiteId, order).catch(() =>
        message.error("Failed to save order"),
      );
      return reordered;
    });
  };

  // Citrix runs (data-driven) go through the reusable CitrixRunModal.
  const handleRun = () => {
    if (isCitrix) {
      setRunModalOpen(true);
      return;
    }
    setRunning(true);
    runUISuite(suite.suiteId, email)
      .then((res) => {
        const { jobId } = res.data;
        message.info(`Job ${jobId} started`);
        pollJobStatus(jobId)
          .then(() => {
            showNotice(
              "success",
              "UI Test Complete",
              `Suite "${suite.suiteName}" passed.`,
            );
            setRunning(false);
          })
          .catch((jobData) => {
            showNotice(
              "error",
              "UI Test Failed",
              jobData?.errorDescription || "Unknown error",
            );
            setRunning(false);
          });
      })
      .catch(() => {
        message.error("Failed to start");
        setRunning(false);
      });
  };

  const actionsByCategory = actions.reduce((acc, a) => {
    if (!acc[a.category]) acc[a.category] = [];
    acc[a.category].push(a);
    return acc;
  }, {});

  const meta = selectedAction ? getActionMeta(selectedAction) : {};

  // ─── Table columns ─────────────────────────────────────────
  const columns = [
    // ── Drag handle (separate column like MasterSuiteNewTable) ─
    {
      key: "sort",
      align: "center",
      width: "4%",
      render: () => <DragHandle />,
    },
    // ── Sequence number (separate column) ──────────────────────
    {
      title: "#",
      key: "seqNo",
      width: "4%",
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, color: "#888" }}>{index + 1}</span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "16%",
      render: (action) => {
        const m = getActionMeta(action);
        return (
          <Tag color={CATEGORY_COLORS[m.category] || "default"}>{action}</Tag>
        );
      },
    },
    {
      title: "Locator",
      dataIndex: "locator",
      key: "locator",
      width: "20%",
      render: (text) =>
        text ? (
          <code
            style={{
              fontSize: 11,
              background: "#f5f5f5",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            {text}
          </code>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      width: "20%",
      render: (text) =>
        text ? (
          <span style={{ fontSize: 12 }}>
            {text.length > 45 ? text.substring(0, 45) + "…" : text}
          </span>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <span style={{ fontSize: 12, color: "#666" }}>{text || "—"}</span>
      ),
    },
    {
      title: "Flags",
      key: "flags",
      width: "8%",
      align: "center",
      render: (_, record) => (
        <Space size={6}>
          {record.screenshotOnFail && (
            <Tooltip title="Screenshot on fail">
              <CameraOutlined style={{ color: "#1890ff" }} />
            </Tooltip>
          )}
          {record.stopOnFail && (
            <Tooltip title="Stop on fail">
              <ThunderboltOutlined style={{ color: "#ff4d4f" }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "9%",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          />
          <Popconfirm
            title="Delete this step?"
            onConfirm={() => handleDeleteStep(record.stepId)}
            okType="danger"
            okText="Delete"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!suite) return null;

  return (
    <div className="db-container">
      <PageHeader
        onBack={() => navigate("/ui-automation")}
        icon={<RobotOutlined />}
        title={suite.suiteName}
        subtitle={
          isCitrix ? (
            <>
              <Tag color="purple" style={{ marginRight: 4 }}>
                Citrix
              </Tag>
              OculiX image &amp; OCR
            </>
          ) : (
            <>
              {suite.baseUrl} · {suite.browser} ·{" "}
              {suite.headless ? "Headless" : "Headed"}
            </>
          )
        }
        actions={
          <>
            <Badge count={steps.length} color="#1890ff">
              <Button icon={<PlusOutlined />} onClick={openAddModal}>
                Add Step
              </Button>
            </Badge>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={running}
              onClick={handleRun}
              disabled={steps.length === 0}
            >
              Run Suite
            </Button>
          </>
        }
      />

      <Row className="table-container">
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={steps.map((s) => s.stepId)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              loading={loading}
              columns={columns}
              dataSource={steps}
              rowKey="stepId"
              components={{ body: { row: DraggableRow } }}
              pagination={false}
              size="small"
              locale={{
                emptyText: "No steps yet — click Add Step to get started",
              }}
            />
          </SortableContext>
        </DndContext>
      </Row>

      {/* ─── Add / Edit step modal ─────────────────────────── */}
      <Modal
        open={stepModalOpen}
        onCancel={() => setStepModalOpen(false)}
        title={editingStep ? `Edit Step ${editingStep.seqNo}` : "Add Step"}
        onOk={handleSaveStep}
        okText={
          <>
            <SaveOutlined /> Save Step
          </>
        }
        destroyOnClose
        centered
        width={580}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {/* Action picker */}
          <Form.Item
            name="action"
            label="Action"
            rules={[{ required: true, message: "Please select an action" }]}
          >
            <Select
              showSearch
              placeholder="Select action..."
              onChange={(val) => {
                setSelectedAction(val);
                // Clear locator/value when action changes
                // so stale values from a previous action don't carry over
                form.setFieldsValue({ locator: undefined, value: undefined });
              }}
              filterOption={(input, option) => {
                if (!input) return true;
                const q = input.toLowerCase();
                const name = (option?.value ?? "").toLowerCase();
                if (name.includes(q)) return true;
                const hint = (
                  getActionMeta(option?.value)?.hint ?? ""
                ).toLowerCase();
                return hint.includes(q);
              }}
            >
              {Object.entries(actionsByCategory).map(([category, acts]) => (
                <Select.OptGroup
                  key={category}
                  label={
                    <Tag color={CATEGORY_COLORS[category]}>{category}</Tag>
                  }
                >
                  {acts.map((a) => (
                    <Option key={a.action} value={a.action}>
                      <Space>
                        <span style={{ fontWeight: 500 }}>{a.action}</span>
                        <span style={{ fontSize: 11, color: "#aaa" }}>
                          — {a.hint}
                        </span>
                      </Space>
                    </Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>

          {/* Locator — field type depends on action metadata (image / text / CSS) */}
          {selectedAction && meta.locatorLabel && (
            <Form.Item
              name="locator"
              label={
                meta.locatorRequired
                  ? meta.locatorLabel === "locator"
                    ? "Locator (CSS selector or XPath)"
                    : meta.locatorLabel
                  : `${meta.locatorLabel} (optional)`
              }
              rules={
                meta.locatorRequired
                  ? [
                      {
                        required: true,
                        message: `${meta.locatorLabel || "Locator"} is required`,
                      },
                    ]
                  : []
              }
              extra={
                !isCitrix ? (
                  <span style={{ fontSize: 11, color: "#888" }}>
                    e.g. <code>#submit-btn</code>,{" "}
                    <code>button:has-text("Login")</code>,{" "}
                    <code>.form input[type=email]</code>
                  </span>
                ) : meta.locatorType === "image" ? (
                  <span style={{ fontSize: 11, color: "#888" }}>
                    Capture or upload the on-screen element OculiX should match.
                  </span>
                ) : null
              }
            >
              {meta.locatorType === "image" ? (
                <ReferenceImagePicker />
              ) : (
                <Input
                  placeholder={
                    isCitrix
                      ? "e.g. variable name or anchor text"
                      : 'e.g. #submit-btn or button:has-text("Submit")'
                  }
                />
              )}
            </Form.Item>
          )}

          {/* Value — required */}
          {meta.valueRequired && (
            <Form.Item
              name="value"
              label={meta.valueLabel === "value" ? "Value" : meta.valueLabel}
              rules={[
                {
                  required: true,
                  message: `${meta.valueLabel || "Value"} is required`,
                },
              ]}
            >
              {selectedAction === "EXECUTE_SCRIPT" ||
              selectedAction === "EVAL_AND_STORE" ? (
                <TextArea
                  rows={4}
                  placeholder="JavaScript code..."
                  style={{ fontFamily: "monospace" }}
                />
              ) : (
                <Input placeholder={meta.hint || "Enter value"} />
              )}
            </Form.Item>
          )}

          {/* Value — optional */}
          {selectedAction && !meta.valueRequired && meta.valueLabel && (
            <Form.Item name="value" label={`${meta.valueLabel} (optional)`}>
              <Input placeholder="Optional value" />
            </Form.Item>
          )}

          {/* Citrix: data-binding hint for the value field */}
          {isCitrix && meta.valueLabel && (
            <div
              style={{
                fontSize: 11,
                color: "#888",
                marginTop: -8,
                marginBottom: 10,
              }}
            >
              Bind a spreadsheet column with <code>{`{{ColumnName}}`}</code>{" "}
              (e.g. <code>{`{{Ticker}}`}</code>) — resolved per row at run time.
              Plain text runs the same every row; mixing is allowed (
              <code>{`ORD-{{TradeId}}`}</code>).
            </div>
          )}

          {/* Citrix: OculiX match knobs for image actions */}
          {isCitrix && meta.locatorType === "image" && (
            <Row gutter={16}>
              <Col span={9}>
                <Form.Item
                  name="similarity"
                  label="Match similarity"
                  extra={
                    <span style={{ fontSize: 11, color: "#888" }}>
                      0.5–1.0 (higher = stricter)
                    </span>
                  }
                >
                  <InputNumber
                    min={0.5}
                    max={1}
                    step={0.05}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={15}>
                <Form.Item
                  name="region"
                  label="Search region (optional)"
                  extra={
                    <span style={{ fontSize: 11, color: "#888" }}>
                      <code>x,y,w,h</code> — limit where OculiX looks
                    </span>
                  }
                >
                  <Input placeholder="e.g. 0,0,800,600" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Citrix: OCR region for text-reading actions */}
          {isCitrix &&
            (selectedAction === "READ_TEXT_OCR" ||
              selectedAction === "ASSERT_TEXT") && (
              <Form.Item
                name="ocrRegion"
                label="OCR region (optional)"
                extra={
                  <span style={{ fontSize: 11, color: "#888" }}>
                    <code>x,y,w,h</code> — the screen area to OCR-read
                  </span>
                }
              >
                <Input placeholder="e.g. 120,300,400,40" />
              </Form.Item>
            )}

          <Form.Item name="description" label="Description (optional)">
            <Input placeholder="What does this step do?" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="screenshotOnFail"
                label="Screenshot on fail"
                valuePropName="checked"
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="stopOnFail"
                label="Stop suite on fail"
                valuePropName="checked"
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ─── Citrix data-driven run (reusable) ────────────────── */}
      <CitrixRunModal
        open={runModalOpen}
        onClose={() => setRunModalOpen(false)}
        suite={suite}
      />
    </div>
  );
}

export default UIAutomationBuilder;
